/**
 * THE SCAN BEHIND THE DYNAMIC SPOTLIGHT TOUR.
 *
 * Reads the screen the user is ACTUALLY looking at — whichever tab is on top, whichever panel is
 * open, whatever their permissions rendered — and returns the controls on it, in reading order,
 * each with the name a person would call it by. `WalkthroughOverlay` turns that into steps.
 *
 * Why scan at all when `walkthroughs.ts` exists: a hand-written tour names the controls that
 * existed when it was written and can only guess which of them this user can see. The scan names
 * exactly what is on screen, so a tour can no longer point at nothing, or skip a control nobody
 * wrote a step for. The curated text is still used — for any control that carries a
 * `data-csm-step` anchor — because it was checked against the component (`dynamicTour.ts`).
 *
 * THREE RULES
 * -----------
 * 1. **A control that repeats is ONE step.** The card hamburger on two hundred cards, the Approve
 *    button in every lane: a person needs to be told once. Repeats are collapsed on kind + label
 *    with digits normalised ("Actions for 10023" and "Actions for 10024" are one control), and
 *    the first in reading order is the one spotlighted — the top-left one, same as the curated
 *    tours' first-match rule.
 * 2. **An anchored group is one step.** A `data-csm-step` on a compact wrapper (a picker and its
 *    clear button, a search box and its icon) is spotlighted as the unit the curated tour meant.
 *    A LARGE anchored region (the whole board) is a step of its own and its controls are still
 *    scanned individually — otherwise the tour would describe the board and nothing on it.
 * 3. **Never the tour's own UI, never the chat panel, never the `?` that started it.**
 *
 * It is pure DOM with no React and no store, so it is testable in jsdom — where nothing has a
 * size, which is why `isVisible` and `rectOf` are injectable.
 */

export interface ScannedControl {
  /** Stable for this scan only: `t1`, `t2`… Stamped on the element as `TOUR_ATTRIBUTE`. */
  readonly id: string;
  readonly element: HTMLElement;
  /** The `data-csm-step` value, when the unit carries one. */
  readonly anchor?: string;
  /** button, text box, dropdown, tab, … — plain words, sent to the model as-is. */
  readonly kind: string;
  /** The accessible name, trimmed. May be empty only for an anchored unit. */
  readonly label: string;
  /** The nearest heading or labelled region, when there is one. */
  readonly context?: string;
  /** disabled, checked, selected, expanded — comma-joined, or undefined. */
  readonly state?: string;
}

export interface ScanOptions {
  /** Most steps a tour may have. Anchored controls are kept first when trimming. */
  readonly max?: number;
  readonly isVisible?: (element: HTMLElement) => boolean;
  readonly rectOf?: (element: HTMLElement) => { top: number; left: number; width: number; height: number };
}

/** The attribute a scanned unit is stamped with, so the overlay can find it by id. */
export const TOUR_ATTRIBUTE = "data-csm-tour";

/** The curated-anchor attribute. Same value as the overlay's `STEP_ATTRIBUTE`. */
const ANCHOR_ATTRIBUTE = "data-csm-step";

/**
 * A scanned tour has more steps than a curated one — it covers everything — but past this it
 * stops being a pass over one screen. The card shows "Step 3 of 24", Back/Next/Escape work
 * throughout, so length costs the reader one press, not the screen.
 */
export const DEFAULT_MAX_TOUR_STEPS = 30;

const INTERACTIVE = [
  "button",
  "a[href]",
  "input:not([type='hidden'])",
  "select",
  "textarea",
  "[role='button']",
  "[role='link']",
  "[role='tab']",
  "[role='combobox']",
  "[role='checkbox']",
  "[role='switch']",
  "[role='radio']",
  "[role='menuitem']",
  "[role='searchbox']",
  "[role='textbox']",
  "[contenteditable='true']",
].join(",");

/**
 * Where the scan never looks. The tour's own card and scrim are marked `data-csm-tour-ui`; the
 * chat panel is the `CoSMo` dialog; the `?` is the button that started this.
 */
const EXCLUDED = [
  "[data-csm-tour-ui]",
  "[role='dialog'][aria-label='CoSMo']",
  "[data-testid='screen-help-button']",
  "[aria-hidden='true']",
  "[inert]",
].join(",");

/** An anchored wrapper at or under this share of the viewport is spotlighted as one unit. */
const COMPACT_SHARE = 0.15;

/** Items whose tops are within this many pixels are one row for reading order. */
const ROW_BAND = 24;

const MAX_LABEL = 60;

const defaultRect = (element: HTMLElement) => element.getBoundingClientRect();

const defaultVisible = (element: HTMLElement): boolean => {
  if (!element.isConnected) return false;
  const rect = element.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return false;

  const style = window.getComputedStyle(element);
  if (style.visibility === "hidden" || style.display === "none" || Number(style.opacity) === 0) {
    return false;
  }

  // Something behind a collapsed ancestor still has a box in some layouts; `checkVisibility`
  // knows better where the browser has it.
  const check = (element as any).checkVisibility; // eslint-disable-line
  return typeof check === "function" ? check.call(element) : true;
};

const squash = (value: string | null | undefined): string =>
  (value ?? "").replace(/\s+/g, " ").trim();

const clip = (value: string, max = MAX_LABEL): string =>
  value.length <= max ? value : `${value.slice(0, max - 1).trimEnd()}…`;

/** "/static/media/Filter-Clear.4f3a2b.svg" → "Filter clear". Icon-only buttons are common here. */
const nameFromIcon = (src: string | null): string => {
  if (!src) return "";
  const file = src.split(/[\\/]/).pop() ?? "";
  const stem = file.split(".")[0] ?? "";
  if (!stem || /^data:/.test(src)) return "";
  const words = stem
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
};

/**
 * The name a person would call the control by — an approximation of the accessible name, in
 * the order a screen reader would look.
 */
export const labelOf = (element: HTMLElement): string => {
  const aria = squash(element.getAttribute("aria-label"));
  if (aria) return clip(aria);

  const labelledBy = element.getAttribute("aria-labelledby");
  if (labelledBy) {
    const text = squash(
      labelledBy
        .split(/\s+/)
        .map((id) => document.getElementById(id)?.textContent ?? "")
        .join(" ")
    );
    if (text) return clip(text);
  }

  const field = element as HTMLInputElement;
  if (field.labels && field.labels.length > 0) {
    const text = squash(Array.from(field.labels).map((l) => l.textContent).join(" "));
    if (text) return clip(text);
  }

  const title = squash(element.getAttribute("title"));
  const placeholder = squash(element.getAttribute("placeholder"));

  const tag = element.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") {
    if (placeholder) return clip(placeholder);
    if (title) return clip(title);
    const wrapperLabel = squash(
      element.closest(".e-float-input, .e-input-group, .e-control-wrapper")?.querySelector(
        ".e-float-text, label"
      )?.textContent
    );
    if (wrapperLabel) return clip(wrapperLabel);
    return clip(squash(element.getAttribute("name")));
  }

  const text = squash(element.innerText ?? element.textContent);
  if (text) return clip(text);
  if (title) return clip(title);

  const img = element.querySelector("img");
  const alt = squash(img?.getAttribute("alt"));
  if (alt) return clip(alt);

  return nameFromIcon(img?.getAttribute("src") ?? null);
};

const ROLE_KIND: Record<string, string> = {
  button: "button",
  link: "link",
  tab: "tab",
  combobox: "dropdown",
  checkbox: "checkbox",
  switch: "switch",
  radio: "option",
  menuitem: "menu item",
  searchbox: "search box",
  textbox: "text box",
};

const INPUT_KIND: Record<string, string> = {
  checkbox: "checkbox",
  radio: "option",
  range: "slider",
  date: "date picker",
  "datetime-local": "date picker",
  time: "time picker",
  number: "number box",
  search: "search box",
  file: "file picker",
  submit: "button",
  button: "button",
  reset: "button",
};

export const kindOf = (element: HTMLElement): string => {
  const role = element.getAttribute("role");
  if (role && ROLE_KIND[role]) return ROLE_KIND[role];

  switch (element.tagName.toLowerCase()) {
    case "button":
      return "button";
    case "a":
      return "link";
    case "select":
      return "dropdown";
    case "textarea":
      return "text box";
    case "input":
      return INPUT_KIND[(element as HTMLInputElement).type] ?? "text box";
    default:
      return element.matches(INTERACTIVE) ? "control" : "area";
  }
};

export const stateOf = (element: HTMLElement): string | undefined => {
  const states: string[] = [];

  if (
    (element as HTMLButtonElement).disabled ||
    element.getAttribute("aria-disabled") === "true" ||
    element.classList.contains("e-disabled")
  ) {
    states.push("disabled");
  }
  if (element.getAttribute("aria-checked") === "true" || (element as HTMLInputElement).checked) {
    states.push("checked");
  }
  if (element.getAttribute("aria-selected") === "true") states.push("selected");
  if (element.getAttribute("aria-expanded") === "true") states.push("expanded");

  return states.length ? states.join(", ") : undefined;
};

/** The nearest heading or labelled region, so "Save" on a panel can be told from "Save" on a grid. */
export const contextOf = (element: HTMLElement): string | undefined => {
  let node: HTMLElement | null = element.parentElement;

  for (let depth = 0; node && depth < 6; depth += 1, node = node.parentElement) {
    const role = node.getAttribute("role");
    if (role && ["region", "dialog", "toolbar", "tablist", "group", "navigation", "tabpanel"].includes(role)) {
      const label = squash(node.getAttribute("aria-label"));
      if (label) return clip(label);
    }

    const heading = node.querySelector<HTMLElement>("h1, h2, h3, h4, legend, [role='heading']");
    if (heading && !heading.contains(element)) {
      const text = squash(heading.textContent);
      if (text) return clip(text);
    }
  }

  return undefined;
};

/** Kind + label with numbers normalised: one step per repeated control. */
const signatureOf = (kind: string, label: string, anchor: string | undefined): string =>
  `${anchor ?? ""}|${kind}|${label.toLowerCase().replace(/\d+/g, "#")}`;

/**
 * Scan `root` for the controls a tour should show. Stamps each returned unit with
 * `TOUR_ATTRIBUTE` — call `clearTourStamps` when the tour ends.
 */
export const scanControls = (root: ParentNode = document.body, options: ScanOptions = {}): ScannedControl[] => {
  const isVisible = options.isVisible ?? defaultVisible;
  const rectOf = options.rectOf ?? defaultRect;
  const max = options.max ?? DEFAULT_MAX_TOUR_STEPS;
  const viewportArea = Math.max(1, window.innerWidth * window.innerHeight);

  clearTourStamps(root);

  const isCompact = (element: HTMLElement) => {
    const rect = rectOf(element);
    return rect.width * rect.height <= viewportArea * COMPACT_SHARE;
  };

  const units: HTMLElement[] = [];
  const taken = new Set<HTMLElement>();

  const candidates = Array.from(
    root.querySelectorAll<HTMLElement>(`${INTERACTIVE}, [${ANCHOR_ATTRIBUTE}]`)
  );

  for (const element of candidates) {
    if (element.closest(EXCLUDED)) continue;

    // Rule 2: a control inside a COMPACT anchored wrapper is that wrapper's step.
    const wrapper = element.parentElement?.closest<HTMLElement>(`[${ANCHOR_ATTRIBUTE}]`);
    const unit = wrapper && !wrapper.closest(EXCLUDED) && isCompact(wrapper) ? wrapper : element;

    if (taken.has(unit) || !isVisible(unit)) continue;
    taken.add(unit);
    units.push(unit);
  }

  // Name everything; drop what cannot be named and is not a curated anchor either.
  const named = units
    .map((element) => {
      const anchor = element.getAttribute(ANCHOR_ATTRIBUTE) ?? undefined;
      const kind = kindOf(element);
      const label = labelOf(element);
      return { element, anchor, kind, label, rect: rectOf(element) };
    })
    .filter((u) => u.label || u.anchor);

  // Reading order: content first, the header's chrome last — the `?` was pressed to learn THIS
  // screen, and the header is the same on every one of them.
  const inChrome = (element: HTMLElement) => Boolean(element.closest(".csm-header, header, [role='banner']"));
  named.sort((a, b) => {
    const chrome = Number(inChrome(a.element)) - Number(inChrome(b.element));
    if (chrome !== 0) return chrome;
    const band = Math.round(a.rect.top / ROW_BAND) - Math.round(b.rect.top / ROW_BAND);
    return band !== 0 ? band : a.rect.left - b.rect.left;
  });

  // Rule 1: one step per repeated control.
  const seen = new Set<string>();
  const unique = named.filter((u) => {
    const signature = signatureOf(u.kind, u.label, u.anchor);
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });

  // Over the cap: keep every curated anchor, fill the rest in reading order.
  let kept = unique;
  if (unique.length > max) {
    const anchored = unique.filter((u) => u.anchor).slice(0, max);
    const room = max - anchored.length;
    const others = unique.filter((u) => !u.anchor).slice(0, room);
    const keep = new Set([...anchored, ...others]);
    kept = unique.filter((u) => keep.has(u));
  }

  return kept.map((u, index) => {
    const id = `t${index + 1}`;
    u.element.setAttribute(TOUR_ATTRIBUTE, id);

    return {
      id,
      element: u.element,
      anchor: u.anchor,
      kind: u.anchor && u.kind === "area" ? "area" : u.kind,
      label: u.label,
      context: contextOf(u.element),
      state: stateOf(u.element),
    };
  });
};

/** Remove every stamp a scan left. Safe to call when there are none. */
export const clearTourStamps = (root: ParentNode = document.body): void => {
  root.querySelectorAll(`[${TOUR_ATTRIBUTE}]`).forEach((element) => element.removeAttribute(TOUR_ATTRIBUTE));
};
